//src/contexts/AuthContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { AccessScope, AuthUser, LoginRequest } from "@/types";
import {
  api,
  authToken,
  clearTokens,
  forgotPasswordRequest,
  getAccessToken,
  resetPasswordRequest,
} from "@/lib/apiClient";

type MeGroup = {
  id: string;
  name: string;
  description?: string | null;
};

type MeTenant = {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  tenant_legal_name?: string | null;
  tenant_document?: string | null;
  is_active: boolean;
};

type MeUnit = {
  unit_id: string;
  tenant_id: string;
  unit_name: string;
  unit_legal_name?: string | null;
  unit_document_number?: string | null;
  is_active: boolean;
} | null;

type MeContacts = {
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
};

type MeResponse = {
  user_id: string;
  person_id: string;
  is_active: boolean;
  full_name: string;
  login: string;
  contacts: MeContacts;
  roles: string[];
  groups: MeGroup[];
  permissions: string[];
  access_scope?: AccessScope | null;
  tenant: MeTenant | null;
  unit: MeUnit;
};

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return ctx;
}

export function usePermission(permission: string): boolean {
  const { user } = useAuth();
  return user?.permissions?.includes(permission) ?? false;
}

export function useAnyPermission(permissions: string[]): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return permissions.some((p) => user.permissions?.includes(p));
}

function mapMeToAuthUser(data: MeResponse): AuthUser {
  const permissions = Array.isArray(data.permissions) ? data.permissions : [];
  const accessScope: AccessScope =
    data.access_scope === "platform" || permissions.some((p) => p.startsWith("platform."))
      ? "platform"
      : "tenant";

  return {
    id: data.user_id,
    person_id: data.person_id,
    tenant_id: data.tenant?.tenant_id || "",
    access_scope: accessScope,
    tenant: data.tenant
      ? {
          id: data.tenant.tenant_id,
          name: data.tenant.tenant_name,
          slug: data.tenant.tenant_slug,
          domain: undefined,
          logo_url: undefined,
          primary_color: undefined,
          accent_color: undefined,
          timezone: "America/Sao_Paulo",
          locale: "pt-BR",
          is_active: data.tenant.is_active,
          created_at: "",
          updated_at: "",
        }
      : null,
    email: data.contacts?.email || data.login,
    login: data.login,
    name: data.full_name,
    avatar_url: undefined,
    permissions,
    groups: Array.isArray(data.groups)
      ? data.groups.map((g) => ({
          id: g.id,
          tenant_id: data.tenant?.tenant_id || "",
          name: g.name,
          description: g.description || undefined,
          is_system: false,
          created_at: "",
        }))
      : [],
    roles: Array.isArray(data.roles) ? data.roles : [],
    unit: data.unit
      ? {
          id: data.unit.unit_id,
          tenant_id: data.unit.tenant_id,
          name: data.unit.unit_name,
          legal_name: data.unit.unit_legal_name || undefined,
          document_number: data.unit.unit_document_number || undefined,
          is_active: data.unit.is_active,
        }
      : null,
    phone: data.contacts?.phone || null,
    whatsapp: data.contacts?.whatsapp || null,
    is_active: data.is_active,
    contacts: {
      email: data.contacts?.email || null,
      phone: data.contacts?.phone || null,
      whatsapp: data.contacts?.whatsapp || null,
    },
  };
}

async function fetchMe(): Promise<AuthUser> {
  const data = await api.get<MeResponse>("/me");
  return mapMeToAuthUser(data);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    const me = await fetchMe();
    setUser(me);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      const token = getAccessToken();

      if (!token) {
        if (mounted) setIsLoading(false);
        return;
      }

      try {
        const me = await fetchMe();
        if (mounted) {
          setUser(me);
        }
      } catch {
        clearTokens();
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(async (data: LoginRequest) => {
    setIsLoading(true);

    try {
      await authToken({
        email: data.email.trim(),
        password: data.password,
      });

      const me = await fetchMe();
      setUser(me);
    } catch (err) {
      clearTokens();
      setUser(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await forgotPasswordRequest(email.trim());
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    await resetPasswordRequest(token, password);
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      refreshMe,
      forgotPassword,
      resetPassword,
    }),
    [user, isLoading, login, logout, refreshMe, forgotPassword, resetPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
